import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import AudioPlayer from './components/AudioPlayer';

const DemoCallPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Hear ReceptionGenie Handle a <span className="text-pink-500">Real Call</span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
            Press play. Watch the visuals. See how we answer calls, handle FAQs, and capture leads automatically.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <a
              href="tel:+18886009252"
              className="whitespace-nowrap px-8 py-4 bg-pink-500 hover:bg-pink-600 text-white font-bold rounded-lg transition-all duration-300 cursor-pointer text-lg shadow-lg hover:shadow-xl hover:scale-105"
            >
              <i className="ri-phone-fill mr-2"></i>
              Call the Live Demo
            </a>
            <a
              href="/lost-revenue-calculator"
              className="whitespace-nowrap px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold rounded-lg transition-all duration-300 cursor-pointer text-lg shadow-md hover:shadow-lg"
            >
              <i className="ri-calculator-line mr-2"></i>
              Try the Lost Revenue Calculator
            </a>
          </div>

          {/* Trust Line */}
          <p className="text-sm text-gray-500 italic">
            No scripting. This is an actual call answered by ReceptionGenie.
          </p>
        </div>
      </section>

      {/* Demo Player Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <AudioPlayer />
        </div>
      </section>

      {/* Why This Demo Matters Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Why This Demo Matters
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            This isn't just a cool demo — it's how you protect revenue and grow your business.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1 */}
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-pink-500 rounded-full mb-4">
                <i className="ri-phone-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Never miss a call again</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Every missed call is a lost opportunity. ReceptionGenie answers 24/7.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-purple-500 rounded-full mb-4">
                <i className="ri-star-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Professional responses every time</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                No more rushed answers or hold music. Every caller gets VIP treatment.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-green-500 rounded-full mb-4">
                <i className="ri-line-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Add more revenue without hiring</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                No salaries, no training, no sick days. Just more leads and bookings.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-100 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-blue-500 rounded-full mb-4">
                <i className="ri-user-add-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Captures &amp; converts customers</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                From first call to booked appointment — automatically tracked and followed up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You Just Heard Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16" style={{ fontFamily: "'Poppins', sans-serif" }}>
            What You Just Heard
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center bg-pink-100 rounded-full mb-6">
                <i className="ri-phone-line text-3xl text-pink-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Answered Instantly</h3>
              <p className="text-gray-600 leading-relaxed">
                No missed calls when you're busy or after hours
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mb-6">
                <i className="ri-question-answer-line text-3xl text-purple-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Handled FAQs</h3>
              <p className="text-gray-600 leading-relaxed">
                Gives confident answers without putting customers on hold
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-6">
                <i className="ri-user-add-line text-3xl text-green-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Captured the Lead</h3>
              <p className="text-gray-600 leading-relaxed">
                Collects details and routes the next step automatically
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Try It Live Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Want to hear it live right now?
          </h2>
          
          <a 
            href="tel:+18886009252"
            className="inline-block text-5xl md:text-6xl font-bold text-pink-500 hover:text-pink-600 transition-colors mb-6 cursor-pointer"
          >
            +1-888-600-9252
          </a>
          
          <p className="text-xl text-gray-600 mb-8">
            Call anytime — ReceptionGenie will pick up and run the demo.
          </p>

          <a
            href="tel:+18886009252"
            className="whitespace-nowrap inline-block px-10 py-5 bg-pink-500 hover:bg-pink-600 text-white font-bold rounded-lg transition-all duration-300 cursor-pointer text-xl shadow-xl hover:shadow-2xl hover:scale-105"
          >
            <i className="ri-phone-fill mr-3"></i>
            Call Now
          </a>

          <p className="text-sm text-gray-500 mt-6">
            Available 24/7
          </p>
        </div>
      </section>

      {/* Secondary CTA Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Curious what missed calls are costing you?
          </h2>
          
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Use our free calculator to estimate your lost revenue in under 60 seconds.
          </p>

          <a
            href="/lost-revenue-calculator"
            className="whitespace-nowrap inline-block px-10 py-5 bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-lg transition-all duration-300 cursor-pointer text-xl shadow-xl hover:shadow-2xl hover:scale-105"
          >
            <i className="ri-calculator-line mr-3"></i>
            Open the Lost Revenue Calculator
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DemoCallPage;
